import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  ChildMenuItem,
  ChildParentMenuItem,
  ParentMenuItem,
  ParentMenuItemChild,
} from '../types';
import {
  addPlatformServiceSectionsToParentMenuItems,
  REVENUE_PLATFORM_SERVICE_SECTIONS,
} from '../platformServiceNavigation';
import { fetchWrapper } from '../utils';
import { useLocalStorage } from '@rehooks/local-storage';
import { OneIdEnvironment, useOneIdEnvironment } from './useOneIdEnvironment';

export type RevenueNavigationPrimaryItem = {
  id: string;
  value: string;
  display_value: string;
  icon: string;
  first_level: RevenueNavigationSecondaryItem[];
};

export type RevenueNavigationSecondaryItem = Pick<
  RevenueNavigationPrimaryItem,
  'id' | 'value' | 'display_value'
> & {
  second_level: RevenueNavigationTertiaryItem[];
};

export type RevenueNavigationTertiaryItem = Pick<
  RevenueNavigationPrimaryItem,
  'id' | 'value' | 'display_value'
> & { seq: number };

export type RevenueNavigationResponse = {
  items: RevenueNavigationPrimaryItem[];
};

export enum ZuoraRevenueStorageKeys {
  OrgDefaultValues = 'orgDefaultValues',
  AuthUser = 'auth_user',
  AuthMode = 'auth_mode',
  /** Profile role written by Revenue host after multi-role selection */
  AuthUserRole = 'auth_userrole',
  IntegratedUser = 'integratedUser',
  TenantId = 'ZRevenue_TenantID',
  UserRole = 'ZRevenue_UserRole',
  FirstName = 'ZRevenue_FirstName',
  LastName = 'ZRevenue_LastName',
  UserStatus = 'ZRevenue_UserStatus',
  Email = 'ZRevenue_Email',
  UserName = 'ZRevenue_UserName',
  TenantName = 'ZRevenue_TenantName',
  UserID = 'ZRevenue_UserID',
  UserLanguage = 'ZRevenue_UserLanguage',
  INITIATE_QUALTRICS = 'ZNav.RevenueInitQualtrics',
  SET_TENANT_BANNER_ENV = 'ZNav.SetTenantBannerEnv',
  TenantEnv = 'ZRevenue_Env',
}

export const generateRevenueUrl = ({
  prefix: _prefix,
  primaryItem,
  secondaryItem,
  tertiaryItem,
}: {
  primaryItem: RevenueNavigationPrimaryItem;
  secondaryItem?: RevenueNavigationSecondaryItem;
  tertiaryItem?: RevenueNavigationTertiaryItem;
  prefix: OneIdEnvironment['revenuePrefix'];
}) => {
  const prefix = `${_prefix}/#`;

  const joinItems = (...segments: { value: string }[]) =>
    [
      prefix,
      ...segments.map(({ value }) =>
        // can't encodeURIComponent without breaking other links
        value.toLowerCase().replace(/ /g, '').replace('#', '%23'),
      ),
    ].join('/');

  // Non-standard cases
  if (secondaryItem && secondaryItem.id === 'dashboard_summary_dashboard') {
    if (tertiaryItem) {
      return joinItems(primaryItem, secondaryItem, { value: tertiaryItem.id });
    } else {
      return [prefix, 'dashboard', 'initialDashboard'].join('/');
    }
  }

  // Standard cases
  if (tertiaryItem) {
    return joinItems(primaryItem, tertiaryItem);
  }

  if (secondaryItem) {
    return joinItems(primaryItem, secondaryItem);
  }

  return joinItems(primaryItem);
};

export const getRevenueNavigationQueryParams = (): URLSearchParams => {
  const getOrgDefaultValues = (): {
    dataSecurity?: string;
    orgCount?: string;
  } => {
    try {
      const value = window.localStorage.getItem(
        ZuoraRevenueStorageKeys.OrgDefaultValues,
      );
      if (value) {
        const defaultValues: { dataSecurity?: string; orgCount?: string }[] =
          JSON.parse(value);
        return {
          dataSecurity: defaultValues.find((item) => item.dataSecurity)
            ?.dataSecurity,
          orgCount: defaultValues.find((item) => item.orgCount)?.orgCount,
        };
      } else {
        throw new Error(
          `Missing '${ZuoraRevenueStorageKeys.OrgDefaultValues}' in localStorage`,
        );
      }
    } catch (err) {
      console.error(
        `Unable to parse '${ZuoraRevenueStorageKeys.OrgDefaultValues}': `,
        err,
      );
    }

    return {};
  };

  const { dataSecurity, orgCount } = getOrgDefaultValues();

  return Object.entries({
    user_name: window.localStorage.getItem(ZuoraRevenueStorageKeys.AuthUser),
    data_security: dataSecurity,
    org_count: orgCount,
    sso_mode:
      window.localStorage.getItem(ZuoraRevenueStorageKeys.AuthMode) ===
      'zuora_revpro'
        ? 'Y'
        : 'N',
  }).reduce((params, [key, value]) => {
    if (value) {
      params.set(key, value);
    }

    return params;
  }, new URLSearchParams());
};

const fetchRevenueData =
  ({ revenuePrefix }: OneIdEnvironment) =>
  async (): Promise<ParentMenuItem[]> => {
    const { items } = await fetchWrapper<RevenueNavigationResponse>(
      `${revenuePrefix}/api/revpro/secure/topBar/sidemenu?${getRevenueNavigationQueryParams().toString()}`,
    );

    const menuItems = items.map((primaryItem): ParentMenuItem => {
      return {
        id: primaryItem.id,
        parentId: null,
        label: primaryItem.display_value,
        parentLabel: null,
        children: primaryItem.first_level.map(
          (secondaryItem): ParentMenuItemChild => {
            if (secondaryItem.second_level.length > 0) {
              return {
                id: secondaryItem.id,
                parentId: primaryItem.id,
                label: secondaryItem.display_value,
                parentLabel: primaryItem.display_value,
                children: secondaryItem.second_level.map(
                  (tertiaryItem): ChildMenuItem => {
                    return {
                      id: tertiaryItem.id,
                      parentId: secondaryItem.id,
                      label: tertiaryItem.display_value,
                      parentLabel: secondaryItem.display_value,
                      url: generateRevenueUrl({
                        prefix: revenuePrefix,
                        primaryItem,
                        secondaryItem,
                        tertiaryItem,
                      }),
                    };
                  },
                ),
              } as ChildParentMenuItem;
            } else {
              return {
                id: secondaryItem.id,
                parentId: primaryItem.id,
                label: secondaryItem.display_value,
                parentLabel: primaryItem.display_value,
                url: generateRevenueUrl({
                  prefix: revenuePrefix,
                  primaryItem,
                  secondaryItem,
                }),
              } as ChildMenuItem;
            }
          },
        ),
      };
    });

    return addPlatformServiceSectionsToParentMenuItems(
      menuItems,
      REVENUE_PLATFORM_SERVICE_SECTIONS,
    );
  };

export const useRevenueNavigation = () => {
  const [lastRefreshed] = useLocalStorage('ZNav.RevenueRefreshMenu');
  const { i18n } = useTranslation();
  const environment = useOneIdEnvironment();
  return useQuery<ParentMenuItem[]>({
    queryKey: ['Revenue Navigation', lastRefreshed, i18n.resolvedLanguage],
    queryFn: fetchRevenueData(environment),
  });
};
